import { ethers } from "ethers";

// Custom error name → friendly message
const ERROR_MESSAGES = {
  OnlyOwner: "Only the contract owner can perform this action.",
  OnlyOwnerOrAdmin: "Contract access required.",
  OnlyLogic: "Storage & Logic contracts are not linked — ask the owner to call setLogicContract().",
  OnlyMigrator: "Migration is locked or migrator not set.",
  AlreadyRegistered: "This wallet is already registered.",
  NotRegistered: "You are not registered yet. Please register first.",
  InvalidPackage: "Invalid package selected.",
  AlreadyActive: "This package is already active.",
  PreviousNotActive: "Please activate the previous package first.",
  PackageNotActive: "You need to activate this package first.",
  TransferFailed: "USDT transfer failed. Check your USDT balance and approval.",
  UserIsBlocked: "This account is blocked.",
  NotEligible: "You are not eligible for this action.",
  NoNewIncome: "No new income available.",
  NoIncomeToClaim: "There's nothing to claim right now.",
  CannotReceiveIncome: "Income cannot be received (ceiling limit reached).",
  InvalidLevel: "Invalid matrix level.",
  AmountZero: "Amount cannot be zero.",
  InsufficientRouterPool: "Not enough funds in router pool.",
  NameTooLong: "Name is too long or contains invalid characters.",
  InvalidWallet: "Invalid wallet address.",
  InvalidClub: "Invalid club.",
  CannotBlockAdmin: "Cannot block the contract owner.",
  WalletAlreadyUsed: "This wallet is already in use.",
  AlreadyBlocked: "User is already blocked.",
  NotBlocked: "User is not blocked.",
  LengthMismatch: "Array length mismatch.",
  MigrationLockedError: "Migration has been permanently locked.",
  NotMigrator: "Only the authorized migrator can perform this action.",
  UserIdCollision: "User ID collision — try again.",
  InsufficientAllowance: "USDT allowance is too low — please approve again.",
  TargetMismatch: "Storage and Logic contracts are not paired correctly.",
  InvalidAddress: "Invalid address.",
  MigrationAlreadyComplete: "Migration has already been marked complete.",
  Unauthorized: "Not authorized for this action.",
};

// Pre-compute 4-byte selectors for every known custom error so we can
// decode empty-ish reverts like `0xa1b2c3d4` back to a readable name.
const SELECTOR_TO_NAME = (() => {
  const map = {};
  for (const name of Object.keys(ERROR_MESSAGES)) {
    try {
      const sig = `${name}()`;
      const selector = ethers.utils.id(sig).slice(0, 10).toLowerCase(); // "0xXXXXXXXX"
      map[selector] = name;
    } catch {}
  }
  return map;
})();

// Extract a hex string (0x…) from anywhere in an ethers/viem error object.
function extractHexData(error) {
  if (!error) return "";
  const candidates = [
    error.data,
    error.error?.data,
    error.error?.error?.data,
    error.error?.error?.error?.data,
    error.data?.data,
    error.info?.error?.data,
    error.cause?.data,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.startsWith("0x")) return c.toLowerCase();
  }
  // Sometimes buried in message: "reverted with 0xabcd1234"
  const msg = (error.message || "").toString();
  const m = msg.match(/0x[0-9a-fA-F]{8,}/);
  return m ? m[0].toLowerCase() : "";
}

// Friendly messages for common ERC20 string reverts
const STRING_REVERT_MAP = [
  [/transfer amount exceeds balance/i,   "You don't have enough USDT in your wallet."],
  [/transfer amount exceeds allowance/i, "USDT allowance is too low — please approve again."],
  [/insufficient allowance/i,            "USDT allowance is too low — please approve again."],
  [/insufficient balance/i,              "Insufficient balance."],
  [/erc20: /i,                            null], // pass through cleaned message
];

function friendlyStringRevert(msg) {
  if (!msg) return null;
  for (const [re, mapped] of STRING_REVERT_MAP) {
    if (re.test(msg)) return mapped || msg.replace(/^ERC20:\s*/i, "").trim();
  }
  return null;
}

export function parseError(error) {
  if (!error) return "Something went wrong.";

  // User rejected
  if (error.code === 4001 || error.code === "ACTION_REJECTED" ||
      /user (rejected|denied)/i.test(error.message || "")) {
    return "Transaction rejected by user.";
  }

  // Insufficient funds for gas
  if (error.code === -32000 ||
      /insufficient funds/i.test(error.message || "")) {
    return "Not enough BNB for gas.";
  }

  // Try to decode a 4-byte custom-error selector from any hex data on the error
  const hex = extractHexData(error);
  if (hex && hex.length >= 10) {
    const sel = hex.slice(0, 10);

    // Standard Error(string) — Solidity `require(x, "msg")` and Solidity <0.8 reverts
    if (sel === "0x08c379a0") {
      try {
        const [reason] = ethers.utils.defaultAbiCoder.decode(["string"], "0x" + hex.slice(10));
        const friendly = friendlyStringRevert(reason);
        if (friendly) return friendly;
        if (reason) return reason;
      } catch {}
    }

    // Panic(uint256) — Solidity 0.8+ assertion / overflow / div-by-zero
    if (sel === "0x4e487b71") {
      try {
        const [code] = ethers.utils.defaultAbiCoder.decode(["uint256"], "0x" + hex.slice(10));
        const n = code.toNumber ? code.toNumber() : Number(code);
        const panics = {
          0x01: "Assertion failed.",
          0x11: "Arithmetic overflow or underflow.",
          0x12: "Division by zero.",
          0x21: "Invalid enum value.",
          0x22: "Invalid encoded storage byte array.",
          0x31: "Pop from empty array.",
          0x32: "Array index out of bounds.",
          0x41: "Out of memory.",
          0x51: "Called an invalid internal function.",
        };
        return panics[n] || `Contract panic (0x${n.toString(16)}).`;
      } catch {}
    }

    // Custom error registered in ERROR_MESSAGES
    const name = SELECTOR_TO_NAME[sel];
    if (name) return ERROR_MESSAGES[name] || name;
  }

  // Look for a named custom error mentioned in the message text
  const raw = (error.reason || error.data?.message || error.message || "").toString();
  for (const name of Object.keys(ERROR_MESSAGES)) {
    if (raw.includes(name)) return ERROR_MESSAGES[name];
  }

  // `execution reverted: XYZ`
  const revertMatch = raw.match(/execution reverted:?\s*([^"]+)/i);
  if (revertMatch) {
    const inner = revertMatch[1].trim().replace(/["']/g, "");
    if (ERROR_MESSAGES[inner]) return ERROR_MESSAGES[inner];
    if (inner && inner !== "0x") return inner;
  }

  // If it looks like an empty revert (0x with no selector), give best-effort guidance
  if (hex === "0x" || raw.includes("returned no data")) {
    return "Contract call reverted without a reason. Common causes: USDT allowance missing, referrer ID doesn't exist, or Logic contract isn't linked to Storage.";
  }

  if (/nonce/i.test(raw))   return "Transaction nonce error. Please try again.";
  if (/gas/i.test(raw))     return "Gas estimation failed — the call would revert. Check your inputs.";
  if (/network/i.test(raw)) return "Network error. Check your connection.";

  const firstSentence = raw.split(/[.\n]/)[0].trim();
  return firstSentence || "Transaction failed. Please try again.";
}
